import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';

import 'flickity-imagesloaded';
import {updateOffscreenPanelVisibility} from '../utils/a11y';
import 'flickity-fade';

const selectors = {
  sliderContainer: '[data-slider-container]',
  slide: '[data-slide]',
  sliderDots: '[data-slide-dot]',
  mobileButtons: '[data-mobile-button]',
};

const classes = {
  singleSlide: 'single-slide',
  selectedSlide: 'is-selected',
  isVisible: 'is-visible',
};

const sectionType = 'hero-slider';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      const container = this.container;
      this.activeHalf = '';

      const slider = container.querySelector(selectors.sliderContainer);
      if (!slider) return;

      const heroSlides = slider.querySelectorAll(selectors.slide);
      const sliderDots = Array.from(container.querySelectorAll(selectors.sliderDots));
      const mobileButtons = container.querySelectorAll(selectors.mobileButtons);

      const sliderAutoplayProperty = getComputedStyle(document.documentElement).getPropertyValue(
        '--hero-slider-autoplay-time'
      );

      const sliderAutoplayTime = parseInt(sliderAutoplayProperty.replace('m', '').replace('s', ''), 10);
      const sliderAutoplay = slider.dataset.sliderAutoplay === 'true' ? sliderAutoplayTime * 1000 : false;

      if (slider.classList.contains(classes.singleSlide)) {
        const slide = container.querySelector(selectors.slide);
        slide?.classList.add(classes.selectedSlide);
        return;
      }

      // Clone slides - a bug with flickity-fade causes carousels with 2 slides to flash the other slide on slide-change.
      // a solution found on a github issue is to duplicat the slides.
      const slides = slider.querySelectorAll(selectors.slide);
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        slider.appendChild(clone);
      });

      const applyParentClass = (index) => {
        const activeSlide = index === 1 ? 'left' : 'right';
        const inactiveSlide = index === 1 ? 'right' : 'left';
        const activeClassName = `${activeSlide}-active`;
        const inactiveClassName = `${inactiveSlide}-active`;
        container.classList.remove(inactiveClassName);
        container.classList.add(activeClassName);
      };

      const handleSliderDotUpdate = (currentSlide) => {
        const transformedSlideIndex = currentSlide % 2 ? 2 : 1;
        applyParentClass(transformedSlideIndex);
        sliderDots.forEach((dot) => {
          const dotParentSlideIndex = parseInt(dot.dataset.slideDot, 10);
          const classAction = dotParentSlideIndex === transformedSlideIndex ? 'add' : 'remove';
          dot.classList[classAction]('selected');
        });
      };

      const resetSliderDotClasses = () => {
        sliderDots.forEach((dot) => {
          dot.classList.remove('selected');
          dot.classList.remove('hover-selected');
        });
      };

      const pauseDots = (slideIndex) => {
        sliderDots.forEach((dot) => {
          const dotParentSlideIndex = parseInt(dot.dataset.slideDot, 10);
          if (dotParentSlideIndex === slideIndex) {
            dot.classList.add('hover-selected');
          }
        });
      };

      const handleHoverSlideUpdate = () => {
        const slideIndex = this.activeHalf === 'left' ? 1 : 2;
        resetSliderDotClasses();
        pauseDots(slideIndex);
        applyParentClass(slideIndex);
        this.flickity.pausePlayer();
        this.flickity.select(slideIndex - 1);
      };

      const initMouseListener = () => {
        container.addEventListener('mousemove', (event) => {
          const sliderWidth = container.offsetWidth;
          const halfwayPoint = sliderWidth / 2;
          if (event.clientX < halfwayPoint) {
            const halfValue = 'left';
            if (this.activeHalf !== halfValue) {
              this.activeHalf = halfValue;
              handleHoverSlideUpdate();
            }
          } else {
            const halfValue = 'right';
            if (this.activeHalf !== halfValue) {
              this.activeHalf = halfValue;
              handleHoverSlideUpdate();
            }
          }
        });
      };

      mobileButtons.forEach((mobileButton) => {
        const slideIndex = mobileButton.dataset.slideIndex;
        mobileButton.addEventListener('click', () => {
          this.flickity.select(slideIndex);
          applyParentClass(slideIndex);
          this.flickity.playPlayer();
        });
      });

      this.flickity = new Flickity(slider, {
        prevNextButtons: false,
        autoPlay: sliderAutoplay,
        pageDots: false,
        adaptiveHeight: false,
        watchCSS: true,
        fade: true,
        wrapAround: true,
        draggable: true,
        imagesLoaded: true,
        on: {
          ready() {
            slider.classList.add(classes.isVisible);
            updateOffscreenPanelVisibility(heroSlides);
            initMouseListener();
            if (sliderAutoplay) {
              handleSliderDotUpdate(0);
            } else {
              pauseDots(1);
            }
          },
          change(index) {
            updateOffscreenPanelVisibility(heroSlides);
            applyParentClass(index + 1);
            if (this.player.state === 'playing') {
              handleSliderDotUpdate(index);
            }
            if (this.player.state === 'stopped') {
              resetSliderDotClasses();
              pauseDots(index + 1);
            }
          },
        },
      });

      slider.addEventListener('mouseleave', () => {
        this.activeHalf = '';
        sliderDots.forEach((dot) => {
          dot.classList.remove('selected');
          dot.classList.remove('hover-selected');
        });
        this.flickity.select(0);
        if (sliderAutoplay) {
          this.flickity.unpausePlayer();
          handleSliderDotUpdate(0);
        } else {
          this.activeHalf = 'left';
          pauseDots(1);
        }
      });
    },

    onBlockSelect(event) {
      // Only run in the admin theme customizer
      if (!Shopify.designMode) {
        return;
      }

      this.flickity.pausePlayer();

      // Scroll to selected slide
      const index = Array.from(event.target.parentElement.children).indexOf(event.target);
      this.flickity.select(index);
    },

    onDeselect() {
      // Only run in the admin theme customizer
      if (!Shopify.designMode) {
        return;
      }

      this.flickity.unpausePlayer();
    },
  });
